import { Component } from '@theme/component';

/**
 * A custom element that displays the language selector as a modal dialog.
 *
 * Language switching is delegated to Shopify's native localization form: each
 * option sets the hidden `language_code` input and submits, exactly like the
 * built-in `<select>` did — only the presentation changes.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLFormElement} form - The localization form element.
 * @property {HTMLInputElement} languageInput - The hidden language_code input.
 *
 * @extends {Component<Refs>}
 */
class LocalizationModalComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog?.addEventListener('click', this.#onDialogClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('click', this.#onDialogClick);
  }

  /**
   * Opens the modal.
   */
  open() {
    this.refs.dialog?.showModal();
  }

  /**
   * Closes the modal.
   */
  close() {
    this.refs.dialog?.close();
  }

  /**
   * Selects a language and submits the localization form.
   *
   * @param {string} isoCode - The iso code of the language to select.
   * @param {Event} event - The event object.
   */
  selectLanguage(isoCode, event) {
    event.preventDefault();
    const { form, languageInput } = this.refs;

    if (!form || !languageInput || !isoCode) return;

    languageInput.value = isoCode;
    form.submit();
  }

  /**
   * Closes the modal when the backdrop (the area around the panel) is clicked.
   *
   * @param {MouseEvent} event - The click event.
   */
  #onDialogClick = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };
}

if (!customElements.get('localization-modal-component')) {
  customElements.define('localization-modal-component', LocalizationModalComponent);
}
